import {
    TokenResponse,
    googleLogout,
    useGoogleLogin,
} from "@react-oauth/google";
import Account from "../api/Account";
import { Box, Stack, Typography } from "@mui/material";
import TextField from "./TextField";
import PasswordTextField from "./PasswordField";
import Button from "./Button";
import GoogleIcon from "../img/Google.svg";
import logo from "../img/Logo.svg";
import { getEnvironment } from "../utils/getEnvironment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { FONTS, GLASS_SX, CARD_STYLES } from "../constants/styles";
//import { requestNotificationPermission } from "../utils/pushUtils";

const environment = getEnvironment();

const INDIGO = "#3B2F80";
const INDIGO_LIGHT = "#E8E5F4";
const GRADIENT_TOP = INDIGO;
const GRADIENT_BOTTOM = "#1A1145";
const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const FIELD_SX = {
    bgcolor: INDIGO_LIGHT,
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent !important",
    },
    "& .MuiInputAdornment-root div": {
        backgroundColor: INDIGO + " !important",
    },
} as const;

export const Login = () => {
    const [token, setToken] = useState<TokenResponse | undefined>();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [, setCookie, removeCookie] = useCookies([
        "uuid",
        "brand_id",
        "theme_color",
    ]);
    const [loading, setLoading] = useState(false);

    const passwordInputRef = useRef<HTMLInputElement>(null);

    const onGoogleLoginError = () => console.error("Google login error.");
    const googleLogin = useGoogleLogin({
        onSuccess: (res) => setToken(res),
        onError: () => onGoogleLoginError(),
    });

    const onLoginSuccess = useCallback(
        async (uuid: string) => {
            // Save the UUID in cookies
            setCookie("uuid", uuid, { path: "/", maxAge: 30 * 24 * 60 * 60 }); // 30 days
            localStorage.setItem("uuid", uuid); // Fallback as LocalStorage is being preserved longer in PWAs
            removeCookie("brand_id", { path: "/" });
            removeCookie("theme_color", { path: "/" });

            // Wait for the cookie to be set
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Redirect to home
            window.location.href = "/";
        },
        [setCookie, removeCookie]
    );

    const onLogin = useCallback(async () => {
        setLoading(true);
        try {
            const res = await Account.signIn(email, password);
            if (!res) {
                alert("Email or password is wrong.");
                return;
            }
            onLoginSuccess(res);

            // Request notification permissions
            //await requestNotificationPermission(res, environment.vapid_key);

            /*
            // Optionally request notification permissions after navigation
            setTimeout(async () => {
                await requestNotificationPermission(res, environment.vapid_key);
            }, 100); // Small delay to avoid blocking the navigation
            */
        } catch (err) {
            console.error("Login error: ", err);
        } finally {
            setLoading(false);
        }
    }, [email, password, onLoginSuccess]);

    useEffect(() => {
        const startGoogleSession = async (token: TokenResponse) => {
            setLoading(true);
            try {
                const uuid = await Account.googleSignIn(token.access_token);

                if (!uuid) {
                    googleLogout();
                    return;
                }

                onLoginSuccess(uuid);

                /*
                // Optionally request notification permissions after navigation
                setTimeout(async () => {
                    await requestNotificationPermission(
                        uuid,
                        environment.vapid_key
                    );
                }, 100); // Small delay to avoid blocking the navigation
                */
            } catch (err) {
                console.error("Google login error: ", err);
            } finally {
                setLoading(false);
            }
        };
        if (!token) return;
        startGoogleSession(token);
    }, [token, onLoginSuccess]);

    return (
        <Stack
            width="100%"
            minHeight="100dvh"
            alignItems="center"
            justifyContent="center"
            sx={{
                background: `linear-gradient(180deg, ${GRADIENT_TOP} 0%, ${GRADIENT_BOTTOM} 100%)`,
                position: "relative",
                overflowY: "auto",
                px: 2,
                py: 4,
                boxSizing: "border-box",
                "&::after": {
                    content: '""',
                    position: "fixed",
                    inset: 0,
                    opacity: 0.04,
                    backgroundImage: NOISE_TEXTURE,
                    backgroundRepeat: "repeat",
                    backgroundSize: "128px 128px",
                    pointerEvents: "none",
                },
            }}
        >
            {/* Logo in frosted-glass box */}
            <Box
                sx={{
                    width: 120,
                    height: 120,
                    borderRadius: "32px",
                    ...GLASS_SX,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
                    mb: 2,
                    "@keyframes logoReveal": {
                        from: { opacity: 0, transform: "scale(0.85)" },
                        to: { opacity: 1, transform: "scale(1)" },
                    },
                    animation: "logoReveal 0.5s ease-out",
                }}
            >
                <img
                    src={logo}
                    alt="JEMLOC"
                    style={{
                        width: 72,
                        height: 72,
                        filter: "brightness(0) invert(1)",
                    }}
                />
            </Box>

            {/* Heading */}
            <Typography
                fontFamily={FONTS.display}
                fontSize="26px"
                fontWeight={700}
                color="white"
                lineHeight={1.2}
                sx={{
                    "@keyframes fadeDown": {
                        from: { opacity: 0, transform: "translateY(-8px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeDown 0.5s ease-out 0.08s both",
                }}
            >
                Welcome back
            </Typography>
            <Typography
                fontSize="14px"
                color="rgba(255, 255, 255, 0.65)"
                sx={{
                    animation: "fadeDown 0.5s ease-out 0.14s both",
                    mb: 3,
                }}
            >
                Log in to your admin panel
            </Typography>

            {/* Form card */}
            <Stack
                sx={{
                    ...CARD_STYLES,
                    width: "100%",
                    maxWidth: 400,
                    px: 2.5,
                    py: 3,
                    boxSizing: "border-box",
                    "@keyframes cardSlideUp": {
                        from: { opacity: 0, transform: "translateY(20px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "cardSlideUp 0.5s ease-out 0.2s both",
                }}
                spacing={1.5}
            >
                <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Email"
                    value={email}
                    onChange={(e: any) =>
                        setEmail(e.currentTarget.value.toLocaleLowerCase())
                    }
                    nextRef={passwordInputRef}
                    sx={FIELD_SX}
                />
                <PasswordTextField
                    variant="outlined"
                    fullWidth
                    placeholder="Password"
                    value={password}
                    onChange={(e: any) =>
                        setPassword(e.currentTarget.value)
                    }
                    ref={passwordInputRef}
                    onEnter={onLogin}
                    sx={FIELD_SX}
                />
                <Typography
                    variant="body2"
                    align="right"
                    sx={{
                        cursor: "pointer",
                        width: "100%",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: INDIGO,
                        "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={() =>
                        (window.location.href = "/forgot-password")
                    }
                >
                    Forgot password?
                </Typography>

                <Box sx={{ pt: 0.5 }}>
                    <Button
                        variant="big"
                        disabled={loading}
                        onClick={onLogin}
                        customColor={INDIGO}
                        textColor="white"
                        fullWidth
                        sx={{
                            "&.Mui-disabled": {
                                bgcolor: INDIGO,
                                opacity: 0.6,
                            },
                        }}
                    >
                        Log in
                    </Button>
                </Box>

                {/* Divider */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ py: 0.5 }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            height: "1px",
                            bgcolor: INDIGO_LIGHT,
                        }}
                    />
                    <Typography
                        fontSize="12px"
                        color="text.secondary"
                        fontWeight={500}
                    >
                        or
                    </Typography>
                    <Box
                        sx={{
                            flex: 1,
                            height: "1px",
                            bgcolor: INDIGO_LIGHT,
                        }}
                    />
                </Stack>

                <Button
                    variant="big"
                    icon={GoogleIcon}
                    disabled={loading}
                    onClick={() => googleLogin()}
                    color="outlined"
                    fullWidth
                    sx={{
                        borderColor: INDIGO_LIGHT,
                        "&:hover": { borderColor: INDIGO, bgcolor: "#F5F4FA" },
                    }}
                >
                    Log in with Google
                </Button>
            </Stack>

            {/* Create your app CTA */}
            <Typography
                fontSize="14px"
                color="rgba(255, 255, 255, 0.6)"
                sx={{
                    mt: 3,
                    cursor: "pointer",
                    animation: "fadeDown 0.5s ease-out 0.35s both",
                    transition: "color 0.2s ease",
                    "&:hover": { color: "rgba(255, 255, 255, 0.9)" },
                }}
                onClick={() =>
                    (window.location.href = environment.register_address)
                }
            >
                Don't have an account?{" "}
                <Box
                    component="span"
                    sx={{ color: "white", fontWeight: 600 }}
                >
                    Create your app
                </Box>
            </Typography>
        </Stack>
    );
};
